import { useCallback, useState } from "react";

type ContentType = "text" | "image" | null;

interface ClipboardOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

interface ClipboardResult {
  copyToClipboard: (content: string | HTMLImageElement) => Promise<void>;
  error: string | null;
  success: boolean;
}

const detectContentType = (content: string | HTMLImageElement): ContentType => {
  if (typeof content === "string") {
    if (content.startsWith("data:image/")) {
      return "image";
    }
    return "text";
  }
  if (content instanceof HTMLImageElement) {
    return "image";
  }
  return null;
};

const checkClipboardPermission = async (): Promise<boolean> => {
  try {
    const permissionStatus = await navigator.permissions.query({ name: "clipboard-write" as PermissionName });
    return permissionStatus.state === "granted" || permissionStatus.state === "prompt";
  } catch (error) {
    if (error instanceof TypeError && error.message.includes("clipboard-write")) {
      return true;
    }
    return false;
  }
};

export const useClipboard = ({ onSuccess, onError }: ClipboardOptions = {}): ClipboardResult => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const copyToClipboard = useCallback(
    async (content: string | HTMLImageElement) => {
      setError(null);
      setSuccess(false);

      const hasPermission = await checkClipboardPermission();
      if (!hasPermission) {
        const errorMessage = "Permission denied: Unable to access the clipboard";
        setError(errorMessage);
        onError?.(errorMessage);
        return;
      }

      const contentType = detectContentType(content);
      if (!contentType) {
        const errorMessage = "Unsupported content type";
        setError(errorMessage);
        onError?.(errorMessage);
        return;
      }

      try {
        if (contentType === "text") await navigator.clipboard.writeText(content as string);
        // oxlint-disable-next-line typescript/no-unnecessary-condition -- explicit ContentType discriminant branch
        else if (contentType === "image") {
          const url = typeof content === "string" ? content : content.src;
          const blob = await (await fetch(url)).blob();
          const item = new ClipboardItem({ [blob.type]: blob });
          await navigator.clipboard.write([item]);
        }
        setSuccess(true);
        onSuccess?.();
      } catch (err) {
        const errorMessage = `Copy failed: ${err instanceof DOMException ? err.message : String(err)}`;
        setError(errorMessage);
        onError?.(errorMessage);
      }
    },
    [onError, onSuccess],
  );

  return { copyToClipboard, error, success };
};
